'use client';

import { useCallback } from 'react';
import type { ViewportChangedEvent } from 'ag-grid-community';
import { usePagedFetch } from '@benzinga/hooks';

export const CALENDAR_FETCH_PAGE_SIZE = 100;

const SCROLL_THRESHOLD_ROWS = 15;

type FetchPage<T, P> = (params: P, page: number) => Promise<T[]>;

export function useCalendarPagedFetch<T, P extends object = object>({
  fetchPage,
  initialData,
  initialParams,
}: {
  fetchPage: FetchPage<T, P>;
  initialData?: T[];
  initialParams?: P;
}) {
  const { data, hasMore, loading, loadingMore, loadMore, reset, setData } = usePagedFetch<T, P>({
    fetchPage,
    initialData,
    initialParams,
    pageSize: CALENDAR_FETCH_PAGE_SIZE,
  });

  const onViewportChanged = useCallback(
    (event: ViewportChangedEvent) => {
      const rowCount = event.api.getDisplayedRowCount();
      if (rowCount === 0) return;

      const nearBottom = event.lastRow >= rowCount - SCROLL_THRESHOLD_ROWS;
      // Avoid cascading fetches when every row already fits on screen (common on
      // sparse Analyst/FDA tabs). Only page when the viewport is scrolled or
      // there is overflow below the visible window.
      const viewportHasOverflowOrScroll = event.firstRow > 0 || event.lastRow < rowCount - 1;
      if (nearBottom && viewportHasOverflowOrScroll) {
        loadMore();
      }
    },
    [loadMore],
  );

  return {
    hasMore,
    loading,
    loadingMore,
    onFilterChanged: reset,
    onViewportChanged,
    setTableData: setData,
    tableData: data,
  };
}
